import { ethers } from "ethers";

// Multicall3's canonical deployment; no signing or value transfer.
// Deployment list: https://github.com/mds1/multicall3/blob/main/deployments.json
const ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";
const CODEHASH =
  "0xd5c15df687b16f2ff992fc8d767b4216323184a2bbc6ee2f9c398c318e770891";
// Chains where that exact code was checked at this address: Base Sepolia, and
// DogeOS Chikyu (2026-09-30, present since before its pool's deployment). The
// code hash is still verified at the pinned block before any batch is trusted.
const BATCHED_CHAINS = new Set([84532, 6281971]);
const ABI = new ethers.Interface([
  "function aggregate3((address target,bool allowFailure,bytes callData)[] calls) payable returns((bool success,bytes returnData)[] returnData)",
]);
type Read = {
  to: string;
  data: string;
  resolve: (value: string) => void;
  reject: (error: unknown) => void;
};
const states = new WeakMap<
  ethers.Provider,
  { verified?: Promise<void>; pending: Map<number, Read[]> }
>();

/** Only calls pinned to the same block can share a batch. Never cache values,
 * swallow a revert, or accept a partial batch as a successful balance read. */
export function readPinnedPoolCall(
  provider: ethers.Provider,
  to: string,
  data: string,
  blockTag: number,
  chainId: number,
): Promise<string> {
  if (!BATCHED_CHAINS.has(chainId))
    return provider.call({ to, data, blockTag } as any);
  if (!Number.isSafeInteger(blockTag) || blockTag < 0)
    return Promise.reject(new Error("Invalid balance snapshot block"));
  let state = states.get(provider);
  if (!state) {
    state = { pending: new Map() };
    states.set(provider, state);
  }
  const current = state;
  if (!current.verified) {
    current.verified = provider
      .getCode(ADDRESS, blockTag)
      .then((code) => {
        if (ethers.keccak256(code) !== CODEHASH)
          throw new Error("Balance reader deployment could not be verified");
      })
      .catch((error) => {
        current.verified = undefined;
        throw error;
      });
  }
  // Attach a rejection observer immediately, even if the flush has not started.
  const verified = current.verified;
  void verified.catch(() => undefined);
  return new Promise((resolve, reject) => {
    let batch = current.pending.get(blockTag);
    if (!batch) {
      batch = [];
      current.pending.set(blockTag, batch);
      const reads = batch;
      setTimeout(() => {
        if (current.pending.get(blockTag) === reads)
          current.pending.delete(blockTag);
        void (async () => {
          try {
            await verified;
            const raw = await provider.call({
              to: ADDRESS,
              blockTag,
              data: ABI.encodeFunctionData("aggregate3", [
                reads.map((read) => ({
                  target: read.to,
                  allowFailure: false,
                  callData: read.data,
                })),
              ]),
            } as any);
            const results = ABI.decodeFunctionResult("aggregate3", raw)[0];
            if (
              results.length !== reads.length ||
              results.some((row: any) => row.success !== true)
            )
              throw new Error("Incomplete balance read");
            results.forEach((row: any, index: number) =>
              reads[index].resolve(row.returnData),
            );
          } catch (error) {
            reads.forEach((read) => read.reject(error));
          }
        })();
      }, 0);
    }
    batch.push({ to, data, resolve, reject });
    // Bound calldata and work even for wallets with thousands of notes.
    if (batch.length === 64) current.pending.delete(blockTag);
  });
}
